import React, { useRef, useEffect, useState, useCallback } from "react";

export default function CameraCapture({ onCapture, onClose }) {
  const videoRef   = useRef(null);
  const canvasRef  = useRef(null);
  const streamRef  = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isActive = true;

    async function startCamera() {
      console.log("🎥 [CAMERA] Requesting access...");

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
        });

        console.log("✅ [CAMERA] Stream obtained:", stream);

        if (!isActive) {
          console.log("⚠️ [CAMERA] Component already unmounted, stopping stream");
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;

          console.log("🎥 [CAMERA] Attaching stream to video element");

          const playPromise = videoRef.current.play();

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log("▶️ [CAMERA] Video playing");
                if (isActive) setReady(true);
              })
              .catch((err) => {
                console.error("❌ [CAMERA] Play error:", err);
              });
          }
        } else {
          console.error("❌ [CAMERA] videoRef is NULL");
        }

      } catch (err) {
        console.error("❌ [CAMERA] getUserMedia ERROR:", err);

        if (isActive) {
          setError("Tidak dapat mengakses kamera. Pastikan izin kamera diaktifkan.");
        }
      }
    }

    startCamera();

    return () => {
      console.log("🧹 [CAMERA] Cleaning up stream...");
      isActive = false;

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const capture = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const base64 = canvas.toDataURL("image/jpeg", 0.8).split(",")[1];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    onCapture(base64);
  }, [onCapture]);

  return (
    <div className="camera-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black">
        <span className="text-white font-semibold font-display">Ambil Selfie</span>
        <button onClick={onClose} className="text-white text-2xl leading-none">×</button>
      </div>

      {/* Camera view */}
      <div className="flex-1 relative flex items-center justify-center bg-black overflow-hidden">
        {error ? (
          <p className="text-red-400 text-center px-8 text-sm">{error}</p>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay playsInline muted
              className="w-full h-full object-cover"
              style={{ transform: "scaleX(-1)" }}
            />
            {/* Face guide */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-52 h-64 rounded-full border-2 border-white/60 border-dashed" />
            </div>
          </>
        )}
      </div>

      {/* Capture button */}
      <div className="flex items-center justify-center py-8 bg-black">
        <button
          onClick={capture}
          disabled={!ready || !!error}
          className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 
            disabled:opacity-50 active:scale-95 transition-transform shadow-lg"
        />
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
